import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { RootState } from "../store";
import { PropertyCollection } from "@/interfaces/wishlistInterface";
import apiClient from "../axiosInstance";

const API_URL = process.env.NEXT_PUBLIC_API_URL;

interface WishListDetails {
  wishlistData: PropertyCollection[];
  error: string | null;
  singleWishListData: PropertyCollection | null;
  loading: boolean;
}

const initialState: WishListDetails = {
  wishlistData: [],
  singleWishListData: null,
  error: null,
  loading: false,
};

export const createWishlistCollection = createAsyncThunk(
  "wishlist/createWishlistCollection",
  async (data: { name: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state?.auth?.token;

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };
      const response = await axios.post(
        `${API_URL}/user-property-collection`,
        data,
        config
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to create wishlist collection"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getWishlistCollectionById = createAsyncThunk(
  "wishlist/getWishlistCollectionById",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state?.auth?.token;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await apiClient.get(
        `${API_URL}/user-property-collection/my-collection`,
        config
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch wishlist collection"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const updateWishlistCollection = createAsyncThunk(
  "wishlist/updateWishlistCollection",
  async (
    { propertyId, collectionId }: { propertyId: string; collectionId: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const token = state?.auth?.token;

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      // const response = await axios.patch(
      //   `${API_URL}/user-property-collection/${collectionId}`,
      //   { property_id: propertyId },
      //   config
      // );
      const response = await apiClient.patch(
        `/user-property-collection/${collectionId}`,
        { property_id: propertyId },
        config
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to update wishlist collection"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const deleteWishlistCollection = createAsyncThunk(
  "wishlist/deleteWishlistCollection",
  async ({ id }: { id: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state?.auth?.token;

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.delete(
        `${API_URL}/user-property-collection/${id}`,
        config
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message ||
            "Failed to delete wishlist collection"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const getSingleCollection = createAsyncThunk(
  "wishlist/getSingleCollection",
  async ({ id }: { id: string }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state?.auth?.token;
      const response = await axios.get(
        `${API_URL}/user-property-collection/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("response", response);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch single collection"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

export const updateCollectionName = createAsyncThunk(
  "wishlist/updateCollectionName",
  async (
    { name, id }: { name: string; id: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const token = state?.auth?.token;
      const response = await axios.patch(
        `${API_URL}/user-property-collection/update-collection-name/${id}`,
        { name },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to update collection name"
        );
      } else {
        // Handle other types of errors, if necessary
        return rejectWithValue("An unknown error occurred");
      }
    }
  }
);

const wishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    removeWishListData: (state) => {
      state.wishlistData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWishlistCollectionById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getWishlistCollectionById.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlistData = action.payload;
      })
      .addCase(getWishlistCollectionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createWishlistCollection.pending, (state) => {
        state.loading = true;
      })
      .addCase(createWishlistCollection.fulfilled, (state) => {
        state.loading = false;
        // state.wishlist = action.payload;
      })
      .addCase(createWishlistCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateWishlistCollection.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateWishlistCollection.fulfilled, (state) => {
        state.loading = false;
        // state.wishlist = action.payload;
      })
      .addCase(updateWishlistCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteWishlistCollection.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteWishlistCollection.fulfilled, (state) => {
        state.loading = false;
        // state.wishlist = action.payload;
      })
      .addCase(deleteWishlistCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSingleCollection.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSingleCollection.fulfilled, (state, action) => {
        state.loading = false;
        state.singleWishListData = action.payload;
      })
      .addCase(getSingleCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCollectionName.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCollectionName.fulfilled, (state) => {
        state.loading = false;
        // state.wishlist = action.payload;
      })
      .addCase(updateCollectionName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { removeWishListData } = wishlistSlice.actions;
export default wishlistSlice.reducer;
